'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import ProductImage from './ProductImage.js'
import ProductInfo from './ProductInfo.js'
import GiftSelection from './GiftSelection.js'
import PriceAndActions from './PriceAndActions.js'
import type {
  FreeGift,
  FreeGiftWithSelection,
  ProductCardData,
  PureProductData,
  ProductTag,
  BuyOneGetOneShelfProductCardSemanticName,
} from './types.js'

export interface ProductCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 产品数据（不包含赠品信息） */
  productData: PureProductData
  /** 赠品列表 */
  gifts?: FreeGift[]
  /** 赠品类型 1-> 赠送其中一个赠品， 2 -> 赠送所有赠品 */
  giftType?: 1 | 2
  /** 场景图片 */
  sceneImages: any
  /** 产品标签 */
  tags?: ProductTag[]
  /** 当前选中的赠品SKU（受控组件） */
  selectedGiftSku?: string
  /** 布局模式 */
  layout?: 'horizontal' | 'vertical'
  classNames?: Partial<Record<BuyOneGetOneShelfProductCardSemanticName, string>>
  /** 产品点击回调 */
  onProductClick?: (product: ProductCardData) => void
  /** Learn More 点击回调 */
  onLearnMore?: (product: ProductCardData, selectedGifts: FreeGift[]) => void
  /** Shop Now 点击回调 */
  onShopNow?: (product: ProductCardData, selectedGifts: FreeGift[]) => void
  /** 赠品点击回调 */
  onGiftClick?: (gift: FreeGift, selectedGifts: FreeGift[]) => void
  /** 赠品选择变化回调 */
  onGiftSelectionChange?: (selectedGifts: FreeGift[], product: ProductCardData) => void
  /** 选中赠品SKU变化回调（受控组件） */
  onSelectedGiftSkuChange?: (selectedSku: string | null) => void
}

/**
 * ProductCard - 买赠货架产品卡片
 *
 * @description 用于买一赠一活动的产品展示卡片，左侧展示产品图片和标签，右侧展示产品信息、赠品和购买按钮
 */
const ProductCard = React.forwardRef<HTMLDivElement, ProductCardProps>(
  (
    {
      productData,
      gifts = [],
      giftType = 1,
      selectedGiftSku,
      sceneImages,
      tags,
      layout = 'horizontal',
      className,
      onProductClick,
      onLearnMore,
      onShopNow,
      onGiftClick,
      onGiftSelectionChange,
      classNames,
      ...props
    },
    ref
  ) => {
    const [learnMoreLoading, setLearnMoreLoading] = React.useState(false)
    const [shopNowLoading, setShopNowLoading] = React.useState(false)

    // 内部状态管理赠品的选择状态 - 使用选中的ID数组而不是修改gift对象
    const [selectedGiftIds, setSelectedGiftIds] = React.useState<string[]>(() => {
      if (!gifts || gifts.length === 0) return []

      if (giftType === 2) {
        // gift_type 2 (全选模式) - 选中所有赠品
        return gifts.map((gift: FreeGift) => gift.id)
      } else {
        // gift_type 1 (单选模式) - 默认选中第一个赠品
        return [gifts[0].id]
      }
    })

    // 生成带有选择状态的赠品列表用于渲染
    const giftsWithSelection = React.useMemo(() => {
      if (!gifts) return []

      return gifts.map((gift: FreeGift) => ({
        ...gift,
        selected: selectedGiftIds.includes(gift.id),
      }))
    }, [gifts, selectedGiftIds])

    // 当 gifts 或 giftType 更新时，同步更新内部状态
    React.useEffect(() => {
      if (!gifts || gifts.length === 0) {
        setSelectedGiftIds([])
        return
      }

      if (giftType === 2) {
        // gift_type 2 (全选模式) - 选中所有赠品
        setSelectedGiftIds(gifts.map((gift: FreeGift) => gift.id))
      } else {
        // gift_type 1 (单选模式) - 智能状态管理
        setSelectedGiftIds(prevSelectedIds => {
          // 检查当前选中的礼品是否仍然存在于新的 gifts 列表中
          const currentSelectedId = prevSelectedIds[0]
          const isCurrentSelectionValid =
            currentSelectedId && gifts.some((gift: FreeGift) => gift.id === currentSelectedId)

          // 如果当前选中状态仍然有效，保持不变；否则默认选中第一个
          return isCurrentSelectionValid ? [currentSelectedId] : [gifts[0].id]
        })
      }
    }, [gifts, giftType])

    // 构造完整的产品数据用于回调
    const fullProductData: ProductCardData = React.useMemo(
      () => ({
        ...productData,
        gifts,
        gift_type: giftType,
      }),
      [productData, gifts, giftType]
    )

    const handleGiftClick = (gift: FreeGift) => {
      // gift_type 2 (全选模式) - 不允许取消选择，直接返回
      if (giftType === 2) {
        // 获取当前所有选中的gifts作为第二个参数
        const currentSelectedGifts = giftsWithSelection.filter((g: FreeGiftWithSelection) => g.selected)
        onGiftClick?.(gift, currentSelectedGifts)
        return
      }

      // gift_type 1 (单选模式)
      setSelectedGiftIds(() => {
        const newSelectedIds = [gift.id] // 单选：始终选中当前项，点击已选中项保持状态，点击未选中项切换到该项

        // 计算新的选中gifts作为第二个参数传递给回调
        const newSelectedGifts = gifts?.filter((g: FreeGift) => newSelectedIds.includes(g.id)) || []

        // 调用回调函数，第二个参数是当前选中的gifts
        onGiftClick?.(gift, newSelectedGifts)
        onGiftSelectionChange?.(newSelectedGifts, fullProductData)

        return newSelectedIds
      })
    }

    const handleProductImageClick = () => {
      onProductClick?.(fullProductData)
    }

    const handleLearnMoreClick = async () => {
      const selectedGifts = giftsWithSelection.filter(gift => gift.selected)
      setLearnMoreLoading(true)
      try {
        await onLearnMore?.(fullProductData, selectedGifts)
      } finally {
        setLearnMoreLoading(false)
      }
    }

    const handleShopNowClick = async () => {
      const selectedGifts = giftsWithSelection.filter(gift => gift.selected)
      setShopNowLoading(true)
      try {
        await onShopNow?.(fullProductData, selectedGifts)
      } finally {
        setShopNowLoading(false)
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          'bg-container-primary rounded-box flex  items-stretch overflow-hidden',
          // 根据布局模式调整容器
          layout === 'horizontal'
            ? [
                // 水平布局：flex-row，占满整行
                'laptop:flex-row lg-desktop:min-h-[560px] desktop:min-h-[448px] tablet:min-h-[526px] min-h-[610px] flex-1 flex-col', // 964px + 700px
              ]
            : [
                // 垂直布局：flex-col，适合一行两个
                'flex-0 tablet:flex-1 tablet:flex-nowrap flex-wrap',
                'flex-col justify-between',
                'min-h-[610px] ',
              ],
          onProductClick ? 'cursor-pointer' : '',
          className
        )}
        {...props}
      >
        {/* 产品图片区域 */}
        <ProductImage
          images={sceneImages}
          name={productData.name}
          layout={layout}
          tags={productData.tags || tags}
          onClick={handleProductImageClick}
        />

        {/* 产品信息区域 */}
        <div
          className={cn(
            'bg-container-primary desktop:p-6 flex flex-col justify-between p-4',
            // 根据布局模式调整圆角和尺寸
            layout === 'horizontal'
              ? [
                  'rounded-r-box laptop:rounded-r-box tablet:rounded-b-box tablet:rounded-l-none',
                  // 使用 flex-basis 设置宽度比例
                  'laptop:basis-[39.1%]', // 360px / 921px ≈ 39.1%
                  'desktop:basis-[42.1%]', // 552px / 1312px ≈ 42.1%
                  'lg-desktop:basis-[42.1%]', // 700px / 1664px ≈ 42.1%
                  'shrink-0',
                ]
              : ['rounded-b-box', 'w-full', 'grow']
          )}
        >
          <div className="flex flex-1 flex-col justify-between gap-6">
            {/* 产品标题和描述 */}
            <ProductInfo
              name={productData.name}
              onClick={handleProductImageClick}
              description={productData.description}
              classNames={classNames}
            />

            {/* 免费赠品区域 */}
            <GiftSelection
              freeGiftLabel={productData.freeGiftLabel}
              freeGifts={giftsWithSelection}
              onGiftClick={handleGiftClick}
              giftType={giftType}
              selectedGiftSku={selectedGiftSku}
            />
            <PriceAndActions
              currentPrice={productData.currentPrice ?? ''}
              originalPrice={productData.originalPrice}
              learnMoreText={productData.learnMoreText}
              shopNowText={productData.shopNowText}
              onLearnMore={handleLearnMoreClick}
              onShopNow={handleShopNowClick}
              availableForSale={productData.availableForSale}
              learnMoreLoading={learnMoreLoading}
              shopNowLoading={shopNowLoading}
              trackingTitle={productData.name}
              trackingDescription={productData.description}
              trackingSku={productData.sku}
            />
          </div>
        </div>
      </div>
    )
  }
)

ProductCard.displayName = 'BuyOneGetOneShelf.ProductCard'

export default ProductCard
